"use client";

import { useEffect } from "react";
import gsap from "gsap";
import MagneticButton from "./MagneticButton";
import AmbientCanvas from "./AmbientCanvas";
import BottleVisual from "./BottleVisual";

export default function Hero({ onOrderClick }: { onOrderClick?: () => void }) {
  // Luxury entrance — fade up, softly, one element at a time
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let started = false;
    let tl: gsap.core.Timeline | null = null;

    const start = () => {
      if (started) return;
      started = true;

      tl = gsap.timeline({
        delay: 0.15,
        defaults: { ease: "power3.out" },
      });

      tl.fromTo(
        ".hero-fx",
        { opacity: 0, filter: "blur(8px)" },
        { opacity: 1, filter: "blur(0px)", duration: 1.8, ease: "sine.out" },
        0
      )
        .fromTo(
          ".hero-el",
          { y: 34, opacity: 0 },
          { y: 0, opacity: 1, duration: 1.2, stagger: 0.12 },
          0.35
        )
        .fromTo(
          ".hero-bottle",
          { y: 40, opacity: 0, scale: 0.96 },
          { y: 0, opacity: 1, scale: 1, duration: 1.6, ease: "power2.out", stagger: 0.16 },
          0.6
        )
        .fromTo(
          ".hero-scroll-hint",
          { opacity: 0 },
          { opacity: 1, duration: 0.9 },
          1.5
        );
    };

    window.addEventListener("stygian:loaded", start);
    const fallback = window.setTimeout(start, 2000);

    // Gentle mouse parallax across silk + bottles (prompt: depth)
    const layers = gsap.utils.toArray<HTMLElement>("[data-depth]");
    const quick = layers.map((el) => ({
      x: gsap.quickTo(el, "x", { duration: 1.4, ease: "power3.out" }),
      y: gsap.quickTo(el, "y", { duration: 1.4, ease: "power3.out" }),
      depth: parseFloat(el.dataset.depth || "8"),
    }));

    const handleMouse = (e: MouseEvent) => {
      const nx = e.clientX / window.innerWidth - 0.5;
      const ny = e.clientY / window.innerHeight - 0.5;
      quick.forEach(({ x, y, depth }) => {
        x(nx * depth);
        y(ny * depth);
      });
    };

    window.addEventListener("mousemove", handleMouse);
    return () => {
      window.removeEventListener("mousemove", handleMouse);
      window.removeEventListener("stygian:loaded", start);
      window.clearTimeout(fallback);
      tl?.kill();
    };
  }, []);

  return (
    <section
      id="hero"
      className="hero-section relative min-h-screen w-full overflow-hidden"
      style={{ background: "linear-gradient(180deg, #fbf8f2 0%, #f4efe6 100%)" }}
    >
      {/* Marble stone backdrop */}
      <div className="absolute inset-0 marble" aria-hidden="true" />
      <div className="hairline-grid absolute inset-0 opacity-30" aria-hidden="true" />

      {/* === ATMOSPHERE === */}
      <div
        className="hero-fx absolute inset-0"
        style={{ opacity: 0, filter: "blur(8px)" }}
      >
        {/* Light floating dust */}
        <AmbientCanvas variant="dust" accent="#c9a84c" density={0.5} className="opacity-50" />

        {/* Flowing silk fabric shapes (champagne, ivory, blush) */}
        <div
          data-depth="6"
          className="silk hero-silk left-[-8%] top-[12%] w-[46rem] h-[30rem]"
          style={{ background: "radial-gradient(ellipse at center, rgba(255,252,245,0.9), rgba(248,240,226,0.5) 60%, transparent 75%)" }}
        />
        <div
          data-depth="9"
          className="silk right-[-10%] top-[30%] w-[40rem] h-[34rem]"
          style={{ background: "radial-gradient(ellipse at center, rgba(216,186,128,0.35), rgba(216,186,128,0.12) 60%, transparent 78%)", animationDelay: "-8s" }}
        />
        <div
          data-depth="7"
          className="silk bottom-[-12%] left-[30%] w-[42rem] h-[30rem]"
          style={{ background: "radial-gradient(ellipse at center, rgba(243,227,227,0.55), rgba(243,227,227,0.2) 60%, transparent 78%)", animationDelay: "-16s" }}
        />

        {/* Soft natural light rays */}
        <div className="ray-layer top-[-8%] left-[16%]" />
        <div className="ray-layer top-[22%] right-[8%]" style={{ animationDelay: "-7s" }} />

        {/* Moving sunlight sweep (prompt: luxury light) */}
        <div className="sunbeam top-0 left-0" />

        {/* Colored light pools for the bottles */}
        <div
          className="orb-glow w-[34rem] h-[34rem] -top-32 -right-24"
          style={{ background: "radial-gradient(circle, rgba(122,156,184,0.16) 0%, transparent 65%)" }}
        />
        <div
          className="orb-glow w-[30rem] h-[30rem] bottom-[-8rem] right-[34%]"
          style={{ background: "radial-gradient(circle, rgba(217,165,176,0.15) 0%, transparent 65%)", animationDelay: "-4s" }}
        />
      </div>

      {/* === BOTTLES ON MARBLE PODIUMS === */}
      <div className="absolute inset-0 hidden lg:block z-[5] pointer-events-none">
        <div data-depth="12" className="hero-bottle absolute right-[12%] top-[14%] w-[220px] xl:w-[260px] opacity-0">
          <BottleVisual
            src="/images/blue_bottle.png"
            alt="Wolfsbane — refined blue bottle"
            name="Wolfsbane"
            glow="#5b7d9b"
            floating="float-slower"
            sizes="300px"
            priority
          />
        </div>
        <div data-depth="16" className="hero-bottle absolute right-[33%] bottom-[8%] w-[190px] xl:w-[230px] opacity-0">
          <BottleVisual
            src="/images/pink_bottle.png"
            alt="Belladonna — blush bottle"
            name="Belladonna"
            glow="#c47a8b"
            floating="float-slow"
            sizes="260px"
            priority
          />
        </div>
      </div>

      {/* === CONTENT === */}
      <div className="hero-content relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 min-h-screen flex flex-col justify-center">
        <div className="max-w-2xl">
          <div className="hero-el flex items-center gap-4 mb-8">
            <div className="w-12 h-px bg-[#c9a84c]/60" />
          </div>

          <h1
            className="hero-el font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.98] tracking-tight text-[#221d17] mb-8"
            style={{ fontWeight: 300 }}
          >
            Crafted for
            <br />
            <span className="text-shimmer-gold">Mystery</span>
          </h1>

          <p className="hero-el text-sm md:text-base text-[#6f6759]/80 max-w-md leading-relaxed mb-4 font-light">
            Two signatures, one shadow. Rare botanicals, midnight harvests, and
            months of silent obsession — distilled into fragrance that is far
            less worn than remembered.
          </p>

          <p className="hero-el text-xs text-[#6f6759]/60 max-w-md leading-relaxed mb-10 font-light">
            Belladonna for her. Wolfsbane for him. Each vessel hand-finished,
            each blend aged in darkness.
          </p>

          <div className="hero-el flex flex-wrap gap-4 mb-10">
            <MagneticButton strength={0.3}>
              <button onClick={onOrderClick} className="btn-elegant" data-cursor="pointer">
                <span>Order Now</span>
              </button>
            </MagneticButton>
            <MagneticButton strength={0.3}>
              <a
                href="#collection"
                className="inline-flex items-center justify-center px-8 py-4 text-[10px] tracking-[0.2em] uppercase text-[#8a6a2c]/70 hover:text-[#6f551f] transition-colors duration-300 border border-transparent hover:border-[#c9a84c]/40"
                data-cursor="pointer"
              >
                Discover the Collection
              </a>
            </MagneticButton>
          </div>

          <div className="hero-el flex items-center gap-8">
            <div>
              <p className="font-serif text-2xl text-[#a8843a] mb-1">2</p>
              <p className="text-[8px] tracking-[0.2em] uppercase text-[#6f6759]/60">Signature Scents</p>
            </div>
            <div className="w-px h-10 bg-[#221d17]/10" />
            <div>
              <p className="font-serif text-2xl text-[#a8843a] mb-1">50ml</p>
              <p className="text-[8px] tracking-[0.2em] uppercase text-[#6f6759]/60">Premium Size</p>
            </div>
            <div className="w-px h-10 bg-[#221d17]/10" />
            <div>
              <p className="font-serif text-2xl text-[#a8843a] mb-1">12h+</p>
              <p className="text-[8px] tracking-[0.2em] uppercase text-[#6f6759]/60">Longevity</p>
            </div>
          </div>
        </div>

        {/* Data readout */}
      </div>

      {/* Scroll indicator */}
      <div className="hero-scroll-hint absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 z-10 opacity-0">
        <span className="text-[8px] tracking-[0.3em] uppercase text-[#8a6a2c]/60">
          Scroll
        </span>
        <div className="w-px h-8 bg-gradient-to-b from-[#c9a84c]/50 to-transparent relative overflow-hidden">
          <div
            className="absolute top-0 left-0 w-full h-2 bg-[#c9a84c]/70"
            style={{ animation: "scrollDot 2s ease-in-out infinite" }}
          />
        </div>
      </div>
    </section>
  );
}